import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout';
import type { ErrorMetadata } from '@cbhq/error-vitals-web';
import { ErrorBoundary } from '@cbhq/error-vitals-web';

import { BasicErrorFallback } from '../components/ErrorBoundary/BasicErrorFallback';
import { NavigationContentSpinner } from '../components/Navigation';
import { useOrganization } from '../contexts/OrganizationContext';
import { ApprovalRulesPage } from '../pages/Security/pages/ApprovalRulesPage';
import { SecurityPage } from '../pages/Security/pages/SecurityPage';
import { ROUTE_SECURITY } from '../pages/Security/utils/constants';
import { ROUTE_TEAM } from '../pages/Team/utils/constants';
import { AppExperiments, useExperiments, useIsTreatment } from '../utils/experiments';

const SECURITY_ERROR_BOUNDARY_DEFAULT_METADATA: ErrorMetadata = {
  context: 'error-boundary',
  severity: 'error',
  isHandled: true,
};

function SecurityRoutes(): ReactElement {
  const { isLoaded } = useExperiments();
  const isWorkspaceConsensusTreatment = useIsTreatment(AppExperiments.t0018_7b870e9f);

  if (!isLoaded) {
    return <NavigationContentSpinner />;
  }

  if (!isWorkspaceConsensusTreatment) {
    return <Navigate to={ROUTE_TEAM} replace />;
  }

  return (
    <Routes>
      <Route index element={<SecurityPage />} />
      <Route path="approval-rules" element={<ApprovalRulesPage />} />
      <Route path="*" element={<Navigate to={ROUTE_SECURITY} replace />} />
    </Routes>
  );
}

export default function Security(): ReactElement {
  const { selectedOrganization } = useOrganization();

  return (
    <VStack width="100%" height="100%">
      <ErrorBoundary
        // Remount the boundary when the org changes so a stale cached error
        // for the previous org doesn't keep the fallback stuck after switching.
        key={selectedOrganization?.organizationId}
        name="security-boundary"
        defaultMetadata={SECURITY_ERROR_BOUNDARY_DEFAULT_METADATA}
        fallback={<BasicErrorFallback />}
      >
        <SecurityRoutes />
      </ErrorBoundary>
    </VStack>
  );
}
